import React from 'react';
import { useAwards, useSiteContent } from '@personal-portfolio/queries';
import {
  PageHeader,
  PageState,
  PageErrorState,
  Reveal,
  Timeline,
} from '@/shared/ui';
import { AwardCard } from './components/award-card';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useLocation } from 'react-router';
import { toErrorMessage } from '@/shared/lib/query-error';

export const AwardsPage: React.FC = () => {
  const {
    data: awardsData,
    isPending: awardsLoading,
    isFetching: awardsFetching,
    isPaused: awardsPaused,
    error: awardsError,
    refetch: refetchAwards,
  } = useAwards();
  const error = toErrorMessage(awardsError, awardsPaused);
  const location = useLocation();
  const content = useSiteContent();
  const page = content.pages.awards;

  const handleRetry = () => {
    if (awardsFetching) return;
    void refetchAwards();
  };

  const renderLoading = () => (
    <>
      <PageHeader title={page.title} subtitle={page.subtitle} />
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="animate-pulse">
            <div className="h-20 rounded-xl bg-muted"></div>
          </div>
        ))}
      </div>
    </>
  );

  const renderError = () => (
    <>
      <PageHeader title={page.title} subtitle={page.subtitle} />
      <PageErrorState onRetry={handleRetry} message={error ?? undefined} />
    </>
  );

  const renderContent = () => {
    if (!awardsData?.length) {
      return (
        <>
          <PageHeader title={page.title} subtitle={page.subtitle} />
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-6xl text-muted-foreground">
                <span role="img" aria-label="Medal">
                  🏅
                </span>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-foreground">
                {page.empty.title}
              </h3>
              <p className="text-muted-foreground">{page.empty.message}</p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <PageHeader title={page.title} subtitle={page.subtitle} />

        <Timeline className="mb-16" dotClassName="top-9.5">
          {awardsData.map((award) => (
            <Reveal key={award.id}>
              <AwardCard award={award} />
            </Reveal>
          ))}
        </Timeline>

        <section className="rounded-3xl border border-border bg-muted p-6 sm:p-10">
          <h2 className="mb-8 text-center text-2xl leading-snug font-semibold tracking-tight text-foreground">
            {page.journeyHeading}
          </h2>
          <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 xl:gap-6">
            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-border bg-card p-4 xl:p-5">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-lg font-semibold text-primary-foreground xl:h-12 xl:w-12 xl:rounded-xl xl:text-xl">
                {awardsData.length}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="mb-1 text-sm font-semibold text-card-foreground xl:text-base">
                  {page.stats.awards.label}
                </h3>
                <p className="text-xs leading-tight text-muted-foreground xl:text-sm">
                  {page.stats.awards.caption}
                </p>
              </div>
            </div>

            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-border bg-card p-4 xl:p-5">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-lg font-semibold text-primary-foreground xl:h-12 xl:w-12 xl:rounded-xl xl:text-xl">
                {page.stats.years.value}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="mb-1 text-sm font-semibold text-card-foreground xl:text-base">
                  {page.stats.years.label}
                </h3>
                <p className="text-xs leading-tight text-muted-foreground xl:text-sm">
                  {page.stats.years.caption}
                </p>
              </div>
            </div>

            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-border bg-card p-4 sm:col-span-2 xl:col-span-1 xl:p-5">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-semibold text-primary-foreground xl:h-12 xl:w-12 xl:rounded-xl xl:text-lg">
                {page.stats.place.value}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="mb-1 text-sm font-semibold text-card-foreground xl:text-base">
                  {page.stats.place.label}
                </h3>
                <p className="text-xs leading-tight text-muted-foreground xl:text-sm">
                  {page.stats.place.caption}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <div className="space-y-5 leading-relaxed text-muted-foreground">
              {page.narrative.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{page.metaTitle}</title>
        <link
          rel="canonical"
          href={`${content.site.url}${location.pathname}`}
        />
      </Helmet>
      <PageState
        loading={awardsLoading}
        error={error}
        hasData={!!awardsData}
        renderLoading={renderLoading}
        renderError={renderError}
        renderContent={renderContent}
      />
    </>
  );
};
